import {css} from '@emotion/css';
import {
  CustomerItem,
  customerItemPreviousResaleTypeBadge,
  DEFAULT_MARKET,
  Options,
} from '@remoov/functional';
import {CustomerPortalModalConfirmed, theme} from '@remoov/remoov-components';
import React from 'react';
import {
  ItemsBoardContainer,
  ItemsBoardHeader,
  ItemsBoardLeftContainer,
  ItemsBoardRightContainer,
  ItemsBoardRow,
  ItemsBoardRowWithActionIcon,
  ItemsBoardTitle,
} from '../Common';
import {ConsignmentBoardItem} from '../ConsignmentBoardItem';
import {ItemDetailsModal} from '../ItemDetailsModal';
import downArrow from './downarrow.svg';
import help from './help.svg';
import upArrow from './uparrow.svg';

export type ConsignmentBoardProps = {
  items: CustomerItem[];
  originalItems: CustomerItem[];
  isOpenModal: Maybe<number>;
  setIsOpenModal: (itemId: Maybe<number>) => unknown;
  isSaveNotesModal: Maybe<number>;
  setIsSaveNotesModal: (itemId: Maybe<number>) => unknown;
  isNotMyItemModal: Maybe<number>;
  setNotMyItemModal: (itemId: Maybe<number>) => unknown;
  isRequestReturnModal: Maybe<number>;
  setRequestReturnModal: (itemId: Maybe<number>) => unknown;
  sort: string;
  sortDirection: 'asc' | 'desc';
  setSort: (column: string) => unknown;
  setStatusModal: () => unknown;
  setPublicNotes: (itemId: Maybe<number>) => (value: string) => unknown;
  savePublicNotes: (item: CustomerItem) => unknown;
  categories: Options<number>;
  notMyItem: (item: CustomerItem) => unknown;
  requestReturn: (item: CustomerItem) => unknown;
};

export const ConsignmentBoard = ({
  items,
  originalItems,
  isOpenModal,
  setIsOpenModal,
  isSaveNotesModal,
  setIsSaveNotesModal,
  isNotMyItemModal,
  setNotMyItemModal,
  isRequestReturnModal,
  setRequestReturnModal,
  sort,
  sortDirection,
  setSort,
  setStatusModal,
  setPublicNotes,
  savePublicNotes,
  categories,
  notMyItem,
  requestReturn,
}: ConsignmentBoardProps) => (
  <ItemsBoardContainer>
    <ItemsBoardHeader>
      <ItemsBoardLeftContainer
        className={css({marginLeft: '194px', padding: `${theme.spacings.xsmall} 0`})}
      >
        <ItemsBoardRowWithActionIcon
          className={css({maxWidth: '100px'})}
          onClick={() => setSort('barcode')}
        >
          <ItemsBoardTitle>ID</ItemsBoardTitle>
          {sort !== 'barcode' ? null : sortDirection === 'asc' ? (
            <img src={upArrow} alt='crescent sort' />
          ) : (
            <img src={downArrow} alt='decrescent sort' />
          )}
        </ItemsBoardRowWithActionIcon>
        <ItemsBoardRowWithActionIcon onClick={() => setSort('description')}>
          <ItemsBoardTitle>DESCRIPTION</ItemsBoardTitle>
          {sort !== 'description' ? null : sortDirection === 'asc' ? (
            <img src={upArrow} alt='crescent sort' />
          ) : (
            <img src={downArrow} alt='decrescent sort' />
          )}
        </ItemsBoardRowWithActionIcon>
      </ItemsBoardLeftContainer>
      <ItemsBoardRightContainer className={css({margin: `${theme.spacings.xsmall} 0`})}>
        <ItemsBoardRowWithActionIcon onClick={() => setSort('quantity')}>
          <ItemsBoardTitle>QUANTITY</ItemsBoardTitle>
          {sort !== 'quantity' ? null : sortDirection === 'asc' ? (
            <img src={upArrow} alt='crescent sort' />
          ) : (
            <img src={downArrow} alt='decrescent sort' />
          )}
        </ItemsBoardRowWithActionIcon>
        <ItemsBoardRowWithActionIcon>
          <ItemsBoardTitle onClick={() => setSort('status')}>STATUS</ItemsBoardTitle>
          {sort !== 'status' ? null : sortDirection === 'asc' ? (
            <img src={upArrow} alt='crescent sort' />
          ) : (
            <img src={downArrow} alt='decrescent sort' />
          )}
          <img src={help} alt='help icon' onClick={setStatusModal} />
        </ItemsBoardRowWithActionIcon>
        <ItemsBoardRow>
          <ItemsBoardTitle>SALES PRICE</ItemsBoardTitle>
        </ItemsBoardRow>
        <ItemsBoardRowWithActionIcon onClick={() => setSort('end_of_consignment')}>
          <ItemsBoardTitle>EXPIRES ON</ItemsBoardTitle>
          {sort !== 'end_of_consignment' ? null : sortDirection === 'asc' ? (
            <img src={upArrow} alt='crescent sort' />
          ) : (
            <img src={downArrow} alt='decrescent sort' />
          )}
        </ItemsBoardRowWithActionIcon>
      </ItemsBoardRightContainer>
    </ItemsBoardHeader>
    {items.map((item, index) => (
      <React.Fragment key={`${item.id}-${item.status}`}>
        <ConsignmentBoardItem
          alt={item.material || ''}
          imgUrl={item.small_url || ''}
          id={item.barcode || ''}
          description={item.description || ''}
          quantity={item.quantity || 0}
          status={item.status || ''}
          resaleTypeBadge={customerItemPreviousResaleTypeBadge('consignment', item.status)}
          market={item.market || DEFAULT_MARKET}
          price={item.amount || item.estimate_resale_value || ''}
          expires={item.end_of_consignment || ''}
          publicNotes={item.public_notes || ''}
          originalPublicNotes={originalItems[index].public_notes || ''}
          setPublicNotes={setPublicNotes(item.id)}
          savePublicNotes={() => savePublicNotes(item)}
          itemDetailsClick={() => setIsOpenModal(item.id)}
        />
        {isOpenModal === item.id && (
          <ItemDetailsModal
            isOpen={isOpenModal === item.id}
            setIsOpen={newIsOpen => (newIsOpen ? setIsOpenModal(item.id) : setIsOpenModal(null))}
            code={item.barcode || ''}
            imgUrl={item.big_url || ''}
            imgAlt={item.material || ''}
            category={categories.find(({value}) => value === item.item_category_id)?.label || ''}
            brand={item.brand || ''}
            original_price={item.original_price || ''}
            notMyItem={() => notMyItem(item)}
            requestReturn={item.status === 'For Sale' ? () => requestReturn(item) : null}
          />
        )}
        {isSaveNotesModal === item.id && (
          <CustomerPortalModalConfirmed
            isOpen={isSaveNotesModal === item.id}
            setIsOpen={newIsOpen =>
              newIsOpen ? setIsSaveNotesModal(item.id) : setIsSaveNotesModal(null)
            }
            title='Save Notes'
          >
            Your notes have been saved!
          </CustomerPortalModalConfirmed>
        )}
        {isNotMyItemModal === item.id && (
          <CustomerPortalModalConfirmed
            isOpen={isNotMyItemModal === item.id}
            setIsOpen={newIsOpen =>
              newIsOpen ? setNotMyItemModal(item.id) : setNotMyItemModal(null)
            }
            title='Not my item'
          >
            Item will be removed from your dashboard.
          </CustomerPortalModalConfirmed>
        )}
        {isRequestReturnModal === item.id && (
          <CustomerPortalModalConfirmed
            isOpen={isRequestReturnModal === item.id}
            setIsOpen={newIsOpen =>
              newIsOpen ? setRequestReturnModal(item.id) : setRequestReturnModal(null)
            }
            title='Request Return'
          >
            Return requested successfully!
          </CustomerPortalModalConfirmed>
        )}
      </React.Fragment>
    ))}
  </ItemsBoardContainer>
);
